import type { Linetype, LinetypeElement } from "./linetype";

/**
 * Canonical acad.lin linetype seed — PR-LT1, plan §10.2 / §10.6.
 *
 * This table is the SINGLE SOURCE OF TRUTH for the standard linetypes
 * (Codex#1 F14). `apps/web/src/app/linetypes/standardLinetypes.ts` is
 * refactored to re-import it in PR-LT3 (deferred — NOT touched in PR-LT1).
 *
 * Contents:
 *   - reserved `ByLayer` / `ByBlock` / `Continuous` (0 elements, patternLength 0)
 *   - 8 acad.lin standards with their exact simple patterns
 *   - the `…2` (half-scale) and `…X2` (double-scale) variant of each of the 8
 *     standards, for full acad.lin parity
 *   - the 14 ISO standards `ACAD_ISO02W100`…`ACAD_ISO15W100` from `acadiso.lin`
 *     (mm; copied AS-IS from `standardLinetypes.ts` — see `ISO_DEFS`)
 *
 * UNIT NOTE: the 8 classic families (+ their `…2`/`…X2`) are in acad.lin
 * IMPERIAL INCHES; the ISO families are in acadiso.lin MILLIMETRES. AutoCAD
 * ships these as two separate files in two units — the unified registry carries
 * both side-by-side, exactly as `standardLinetypes.ts` does today.
 *
 * Every entry is a valid `Linetype` (proven by parsing each through
 * `linetypeSchema` in `linetype.test.ts`).
 *
 * Pattern derivation (per plan §10.2): each signed .lin number maps to an
 * element — `> 0` ⇒ `dash{length}`, `< 0` ⇒ `gap{length: |n|}`, `== 0` ⇒
 * `dot`. `patternLength = Σ|signed length|`.
 */

/** Map a single signed .lin number to its pattern element. */
function elementFromSigned(n: number): LinetypeElement {
  if (n > 0) return { kind: "dash", length: n };
  if (n < 0) return { kind: "gap", length: -n };
  return { kind: "dot", length: 0 };
}

/**
 * Inverse of {@link elementFromSigned} — map ONE pattern element back to its
 * signed code-49 number: `dash` → `+length`, `gap` → `-length`, `dot` → `0`.
 *
 * Complex `text` / `shape` elements occupy a host segment whose `length` is a
 * signed code-49 value (usually a gap the glyph is drawn inside); they collapse
 * to that segment's signed length for the purpose of the single-dash-pair
 * renderer (the glyph itself is a DEFERRED shader feature — PR-LT4). A `text` /
 * `shape` host with a non-negative length contributes a dash-equivalent run; a
 * negative one contributes a gap. This keeps the summarised pattern faithful to
 * the segment lengths AutoCAD draws even though the glyph is not yet rendered.
 */
export function signedFromElement(el: LinetypeElement): number {
  switch (el.kind) {
    case "dash":
      return el.length;
    case "gap":
      return -el.length;
    case "dot":
      return 0;
    case "text":
    case "shape":
      // Complex host segment: its `length` is already signed (code 49).
      return el.length;
  }
}

/**
 * Convert a {@link Linetype}'s `elements[]` into the signed code-49 sequence
 * the single-dash-pair renderer (`compileCustomLtype`) consumes:
 * dash → `+len`, gap → `-len`, dot → `0`. PR-LT3 — used by the scene3d render
 * registry to compile a core-seed / custom LTYPE into a `DashConfig`.
 */
export function signedLengthsFromLinetype(lt: {
  elements: ReadonlyArray<LinetypeElement>;
}): number[] {
  return lt.elements.map(signedFromElement);
}

/** Build a simple (dash/gap/dot-only) linetype from its signed .lin numbers. */
function simpleLinetype(
  name: string,
  description: string,
  signed: readonly number[],
): Linetype {
  return {
    name,
    description,
    flags: 0,
    alignment: 65,
    patternLength: signed.reduce((sum, n) => sum + Math.abs(n), 0),
    elements: signed.map(elementFromSigned),
  };
}

/** A reserved linetype: 0 elements, 0 pattern length. */
function reservedLinetype(name: string): Linetype {
  return {
    name,
    description: "",
    flags: 0,
    alignment: 65,
    patternLength: 0,
    elements: [],
  };
}

/**
 * The 8 acad.lin standards, each as `[descriptionGraphic, ...signedNumbers]`.
 * Signed numbers are the canonical acad.lin code-49 sequence (plan §10.2).
 */
const STANDARD_DEFS: ReadonlyArray<{
  readonly name: string;
  readonly description: string;
  readonly signed: readonly number[];
}> = [
  { name: "DASHED", description: "Dashed __ __ __ __ __ __ __ __ __ __ __ __", signed: [0.5, -0.25] },
  { name: "HIDDEN", description: "Hidden __ __ __ __ __ __ __ __ __ __ __ __ __", signed: [0.25, -0.125] },
  {
    name: "CENTER",
    description: "Center ____ _ ____ _ ____ _ ____ _ ____ _ ____",
    signed: [1.25, -0.25, 0.25, -0.25],
  },
  {
    name: "PHANTOM",
    description: "Phantom ______  __  __  ______  __  __  ______",
    signed: [1.25, -0.25, 0.25, -0.25, 0.25, -0.25],
  },
  { name: "DOT", description: "Dot . . . . . . . . . . . . . . . . . . . . . .", signed: [0, -0.25] },
  {
    name: "DASHDOT",
    description: "Dash dot __ . __ . __ . __ . __ . __ . __ . __",
    signed: [0.5, -0.25, 0, -0.25],
  },
  {
    name: "BORDER",
    description: "Border __ __ . __ __ . __ __ . __ __ . __ __ .",
    signed: [0.5, -0.25, 0.5, -0.25, 0, -0.25],
  },
  {
    name: "DIVIDE",
    description: "Divide ____ . . ____ . . ____ . . ____ . . ____",
    signed: [0.5, -0.25, 0, -0.25, 0, -0.25],
  },
];

/**
 * The `…2` (half) variants of each standard are HAND-TUNED in AutoCAD's stock
 * acad.lin — they are NOT a mechanical `base × 0.5`. Codex#2 P2 caught the old
 * `n * 0.5` derivation shipping wrong dashes (e.g. CENTER2 first dash 0.625
 * instead of the real 0.75).
 *
 * Source of truth: AutoCAD stock `acad.lin` `*<NAME>2` definitions (the
 * `A,...code-49...` sequence), cross-checked against
 * `apps/web/src/app/linetypes/standardLinetypes.ts` (mm dash arrays ÷ 25.4).
 * Six of the eight match `standardLinetypes.ts` exactly after the mm→inch
 * conversion (HIDDEN2/CENTER2/DOT2/DASHDOT2/BORDER2/DIVIDE2). Two diverge
 * because `standardLinetypes.ts` is itself imprecise for those rows and the
 * acad.lin source value is used instead:
 *   - DASHED2  → acad.lin `.25,-.125` (standardLinetypes.ts `Dashed` base used
 *                a ×25 not ×25.4 scale, so `Dashed2`/25.4 ≈ 0.2461 — wrong).
 *   - PHANTOM2 → acad.lin `.625,-.125,.125,-.125,.125,-.125` (standardLinetypes.ts
 *                `Phantom2` copy-pasted Center2's 19.05 long dash → would give
 *                0.75; the real acad.lin long dash is 0.625).
 *
 * Each entry is the signed code-49 sequence: `> 0` dash, `< 0` gap, `== 0` dot.
 */
const HALF_SIGNED: Readonly<Record<string, readonly number[]>> = {
  DASHED: [0.25, -0.125],
  HIDDEN: [0.125, -0.0625],
  CENTER: [0.75, -0.125, 0.125, -0.125],
  PHANTOM: [0.625, -0.125, 0.125, -0.125, 0.125, -0.125],
  DOT: [0, -0.125],
  DASHDOT: [0.25, -0.125, 0, -0.125],
  BORDER: [0.25, -0.125, 0.25, -0.125, 0, -0.125],
  DIVIDE: [0.25, -0.125, 0, -0.125, 0, -0.125],
};

/**
 * The 14 ISO standards from AutoCAD's stock `acadiso.lin`
 * (`ACAD_ISO02W100`…`ACAD_ISO15W100`), each as `[descriptionGraphic,
 * ...signedNumbers]`.
 *
 * UNIT — MILLIMETRES (acadiso.lin native unit), NOT inches.
 *   The 8 classic families above are in `acad.lin` IMPERIAL INCHES (DASHED =
 *   `.5,-.25`). The ISO families, however, exist ONLY in `acadiso.lin`, which
 *   is authored in MILLIMETRES — there is NO imperial acad.lin equivalent to
 *   convert from. AutoCAD itself ships these two as SEPARATE files in two
 *   different units; the unified registry carries both side-by-side.
 *
 *   These signed numbers are copied AS-IS from the documented source —
 *   `apps/web/src/app/linetypes/standardLinetypes.ts` (the `ACAD_ISO*` rows,
 *   mm `dashPattern`) — which PR-LT3 will unify against THIS seed. Converting
 *   them to inches would (a) require a unit the source never specifies and (b)
 *   diverge from the values PR-LT3 consumes. The source's near-zero dot
 *   sentinel (`0.01`) is mapped to the seed's canonical signed `0` (dot)
 *   element; every other value becomes a dash (>0) or gap (<0, alternating).
 *
 *   Each entry is the signed code-49 sequence: `> 0` dash, `< 0` gap, `== 0`
 *   dot. The source `dashPattern` stores MAGNITUDES with implied alternating
 *   dash/gap signs (first is a dash) — we re-sign here, and fold the `0.01`
 *   dot sentinel to `0`.
 */
const ISO_DEFS: ReadonlyArray<{
  readonly name: string;
  readonly description: string;
  /** Source `dashPattern` magnitudes (mm); alternating dash/gap, first=dash. */
  readonly mags: readonly number[];
}> = [
  { name: "ACAD_ISO02W100", description: "ISO Dashed", mags: [12, 3] },
  { name: "ACAD_ISO03W100", description: "ISO Dashed space", mags: [12, 18] },
  {
    name: "ACAD_ISO04W100",
    description: "ISO Long-dashed dotted",
    mags: [24, 3, 0.01, 3],
  },
  {
    name: "ACAD_ISO05W100",
    description: "ISO Long-dashed double-dotted",
    mags: [24, 3, 0.01, 3, 0.01, 3],
  },
  {
    name: "ACAD_ISO06W100",
    description: "ISO Long-dashed triple-dotted",
    mags: [24, 3, 0.01, 3, 0.01, 3, 0.01, 3],
  },
  { name: "ACAD_ISO07W100", description: "ISO Dotted", mags: [0.01, 3] },
  {
    name: "ACAD_ISO08W100",
    description: "ISO Long-dashed short-dashed",
    mags: [24, 3, 6, 3],
  },
  {
    name: "ACAD_ISO09W100",
    description: "ISO Long-dashed double-short-dashed",
    mags: [24, 3, 6, 3, 6, 3],
  },
  {
    name: "ACAD_ISO10W100",
    description: "ISO Dashed dotted",
    mags: [12, 3, 0.01, 3],
  },
  {
    name: "ACAD_ISO11W100",
    description: "ISO Double-dashed dotted",
    mags: [12, 3, 12, 3, 0.01, 3],
  },
  {
    name: "ACAD_ISO12W100",
    description: "ISO Dashed double-dotted",
    mags: [12, 3, 0.01, 3, 0.01, 3],
  },
  {
    name: "ACAD_ISO13W100",
    description: "ISO Double-dashed double-dotted",
    mags: [12, 3, 12, 3, 0.01, 3, 0.01, 3],
  },
  {
    name: "ACAD_ISO14W100",
    description: "ISO Dashed triple-dotted",
    mags: [12, 3, 0.01, 3, 0.01, 3, 0.01, 3],
  },
  {
    name: "ACAD_ISO15W100",
    description: "ISO Double-dashed triple-dotted",
    mags: [12, 3, 12, 3, 0.01, 3, 0.01, 3, 0.01, 3],
  },
];

/**
 * Re-sign an ISO source `dashPattern` (magnitudes, first=dash, alternating)
 * into the seed's signed code-49 sequence. The near-zero dot sentinel
 * (`< 0.02`, i.e. the source's `0.01`) folds to signed `0` (dot); otherwise
 * even indices are dashes (positive) and odd indices are gaps (negative).
 */
function isoSignedFromMagnitudes(mags: readonly number[]): number[] {
  return mags.map((mag, i) => {
    if (mag < 0.02) return 0; // dot sentinel → signed 0
    return i % 2 === 0 ? mag : -mag; // even=dash(+), odd=gap(−)
  });
}

/**
 * Build the full seed: reserved entries + each standard + its half (`2`) and
 * double (`X2`) variants + the 14 ISO standards.
 *
 * - `…2` (half) — HAND-TUNED acad.lin values from `HALF_SIGNED` (NOT a
 *   mechanical half; see the comment on `HALF_SIGNED`).
 * - `…X2` (double) — mechanical `base × 2` (the double variants ARE a true 2×
 *   of the base in acad.lin); `0` (dot) stays `0`.
 * - `ACAD_ISO*` — copied AS-IS (mm) from `standardLinetypes.ts`; see `ISO_DEFS`.
 */
function buildStandardLinetypes(): Record<string, Linetype> {
  const out: Record<string, Linetype> = {
    ByLayer: reservedLinetype("ByLayer"),
    ByBlock: reservedLinetype("ByBlock"),
    Continuous: { ...reservedLinetype("Continuous"), description: "Solid line" },
  };
  for (const def of STANDARD_DEFS) {
    out[def.name] = simpleLinetype(def.name, def.description, def.signed);
    // `…2` — hand-tuned acad.lin half variant.
    const half = HALF_SIGNED[def.name];
    out[`${def.name}2`] = simpleLinetype(
      `${def.name}2`,
      `${def.description} (.5x)`,
      half,
    );
    // `…X2` — mechanical double scale.
    const dbl = def.signed.map((n) => n * 2);
    out[`${def.name}X2`] = simpleLinetype(
      `${def.name}X2`,
      `${def.description} (2x)`,
      dbl,
    );
  }
  // ISO (acadiso.lin, mm) — copied AS-IS from standardLinetypes.ts.
  for (const iso of ISO_DEFS) {
    out[iso.name] = simpleLinetype(
      iso.name,
      iso.description,
      isoSignedFromMagnitudes(iso.mags),
    );
  }
  return out;
}

/**
 * Standard acad.lin linetypes, NAME-keyed. Always seeded into a fresh or
 * parsed `DrawingDocument` (via `drawingDocumentSchema.linetypes` default) so
 * the future linetype refcheck can resolve standard names.
 */
export const STANDARD_LINETYPES: Record<string, Linetype> =
  buildStandardLinetypes();
